import { useContext, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Typography, Box, IconButton, Tooltip, CircularProgress, Alert, Snackbar, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, InputAdornment } from '@mui/material';

import { AppContext } from "../../context/context";
import { useFixedRoutes } from "../../context/FixedRoutesContext";
import EditableTypography from "../base_components/EditableTypography";
import {useDrop} from "react-dnd";
import { useAddLayer, useReorderLayers } from "./functions/layerFunctions";
import { logEvent, trackAnalyticsEvent } from "../../services/analytics";
import { ANALYTICS_EVENT_NAMES } from "../../services/eventTaxonomy";

import ShapesLayer from "./shapesLayer";
import MapToolBar from "./mapToolBar";
import ImportWizardModal from "../import-wizard/ImportWizardModal";
import { Point, Line, Polygon, TripImportSquishMeta } from '../../context/context';
import { defaultLayer } from '../../context/defaults';
import { mapService } from '../../services/api/mapService';
import { generateMapThumbnail } from '../../services/thumbnail/thumbnailGenerator';
import MapGalleryModal from '../map/modals/MapGalleryModal';
import { FeatureGate } from '../common/FeatureGate';
import { applyImportedPointLayerColour } from './functions/importLayerColors';
import { Button as OpsButton } from '@liftango/ui-elements';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors, opsElevation } from '../../theme/opsTokens';
import { LAYER_PANEL_CAPTION_SX } from './layerPanelStyles';
import { searchAddress } from '../../services/geocoding';

function buildMapStateSignature(args: {
    mapName: string;
    mapLayers: unknown;
    fleets: unknown;
    fixedRoutes: {
        routes: unknown;
        stops: unknown;
    };
}): string {
    try {
        return JSON.stringify(args);
    } catch (error) {
        console.warn('Unable to serialize map state for unsaved-change detection:', error);
        return `${args.mapName}-${Date.now()}`;
    }
}

// Inner component that uses drop hook (must be inside DndProvider)
function LayersList({ mapLayers, reorderLayers }: { mapLayers: any[], reorderLayers: (source: number, target: number) => void }) {
    // Drop target for layer reordering - handles drops between layers
    const [{ isOver: isLayerDropOver }, layerDropRef] = useDrop<{ layerId: number, layerIndex: number }, void, { isOver: boolean }>({
        accept: 'LAYER',
        collect: (monitor) => ({
            isOver: monitor.isOver()
        }),
        drop: (item, monitor) => {
            // Prevent nested drops
            if (!monitor.didDrop()) {
                const targetIndex = mapLayers.findIndex(l => l.layerId === item.layerId);
                // If dropping on itself or invalid, do nothing
                if (targetIndex === item.layerIndex || targetIndex === -1) return;
                // Reorder the layer
                reorderLayers(item.layerIndex, targetIndex);
            }
        }
    });
    
    const layerCards = mapLayers.map((layer, index) => {
        // Use combination of layerId and index to ensure unique keys
        return <ShapesLayer layerData={layer} key={`layer-${layer.layerId}-${index}`}/>
    });
    
    return (
        <Box
            ref={layerDropRef as unknown as React.RefObject<HTMLDivElement>}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                minHeight: 0,
                p: 0.5,
                borderRadius: 2,
                border: isLayerDropOver ? `1px dashed ${opsColors.brand.primary}` : '1px solid transparent',
                backgroundColor: isLayerDropOver ? 'rgba(244, 122, 53, 0.05)' : 'transparent',
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
            }}>
                {layerCards}
        </Box>
    );
}

type LayersPanelProps = {
    isCollapsed?: boolean;
};

export default function LayersPanel({ isCollapsed = false }: LayersPanelProps) {
    const { t } = useTranslation(['map', 'common']);
    const context = useContext(AppContext);
    const fixedRoutesContext = useFixedRoutes();

    if (!context) {
        throw new Error("LayersPanel must be used within the context provider")
    }

    const {global, mapSettings, mapActions} = context;
    const {mapLayers} = global;
    const {mapName, setMapName} = mapSettings;
    const openSimulationModal = mapActions?.openSimulationModal ?? null;
    const addLayer = useAddLayer();
    const reorderLayers = useReorderLayers();

    const [importModalOpen, setImportModalOpen] = useState(false);
    
    // Add state for save functionality
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
    
    // Add state for map gallery modal
    const [isMapGalleryOpen, setIsMapGalleryOpen] = useState(false);
    
    // Add state to track current map ID (for overwriting existing maps)
    const [currentMapId, setCurrentMapId] = useState<string | null>(null);
    const [pendingLoadedMapSignature, setPendingLoadedMapSignature] = useState<string | null>(null);
    const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
    const [refreshDraftMapName, setRefreshDraftMapName] = useState(mapName);
    const [refreshActionLoading, setRefreshActionLoading] = useState(false);
    const [refreshError, setRefreshError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const bypassBeforeUnloadRef = useRef(false);

    const currentMapSignature = buildMapStateSignature({
        mapName,
        mapLayers,
        fleets: global.fleets,
        fixedRoutes: {
            routes: fixedRoutesContext.routes,
            stops: fixedRoutesContext.stops,
        },
    });
    const [lastSavedSignature, setLastSavedSignature] = useState<string>(currentMapSignature);

    useEffect(() => {
        const handler = () => setImportModalOpen(true);
        window.addEventListener('openImportLayerModal', handler);
        return () => window.removeEventListener('openImportLayerModal', handler);
    }, []);

    useEffect(() => {
        if (pendingLoadedMapSignature === null) {
            return;
        }

        if (currentMapSignature !== pendingLoadedMapSignature) {
            setLastSavedSignature(currentMapSignature);
            setPendingLoadedMapSignature(null);
            return;
        }

        const fallbackTimer = window.setTimeout(() => {
            setLastSavedSignature(currentMapSignature);
            setPendingLoadedMapSignature(null);
        }, 300);

        return () => window.clearTimeout(fallbackTimer);
    }, [currentMapSignature, pendingLoadedMapSignature]);

    const hasUnsavedChanges = currentMapSignature !== lastSavedSignature;

    useEffect(() => {
        if (!refreshConfirmOpen) return;
        setRefreshDraftMapName(mapName);
    }, [mapName, refreshConfirmOpen]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (bypassBeforeUnloadRef.current) {
                return;
            }

            if (!hasUnsavedChanges) {
                return;
            }

            const warningMessage = t('layers.save.beforeUnloadWarning');
            event.preventDefault();
            event.returnValue = warningMessage;
            return warningMessage;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    function editMapTitle(value: string): void {
        setMapName(prev => value)
    }

    function handleImport(
        shapes: (Point | Line | Polygon)[],
        meta?: { tripImportSquish?: TripImportSquishMeta }
    ) {
        handleImportedShapes(global, shapes, meta);
        setImportModalOpen(false);
        // Reset current map ID since we're importing new data
        setCurrentMapId(null);
    }

    const handleRealSaveClick = useCallback(async (options?: { overrideMapName?: string }): Promise<boolean> => {
        if (isSaving) return false;

        const effectiveMapName = (options?.overrideMapName ?? mapName).trim();

        if (!effectiveMapName) {
            setSaveMessage({
                type: 'error',
                text: t('layers.save.errorEnterName'),
            });
            return false;
        }

        if (effectiveMapName !== mapName) {
            setMapName(effectiveMapName);
        }

        if (effectiveMapName.includes('/')) {
            setSaveMessage({
                type: 'error',
                text: t('layers.save.errorSlashInName'),
            });
            return false;
        }
        
        setIsSaving(true);
        setSaveMessage(null);
        
        const startTime = Date.now();
        const isUpdate = !!currentMapId;
        
        try {
            console.log('💾 Saving map:', effectiveMapName);
            console.log('📊 Layers to save:', mapLayers.length);
            
            // Get map container for thumbnail generation
            const mapContainer = document.querySelector('.leaflet-container') as HTMLElement;
            let thumbnail: Blob | null = null;
            
            if (mapContainer) {
                console.log('📸 Generating thumbnail...');
                thumbnail = await generateMapThumbnail(mapContainer);
                if (thumbnail) {
                    console.log('✅ Thumbnail generated successfully');
                } else {
                    console.log('⚠️ Thumbnail generation failed, proceeding without thumbnail');
                }
            } else {
                console.log('⚠️ Map container not found, saving without thumbnail');
            }
            
            // Save the map using our service
            // Use existing map ID if we're editing a loaded map, otherwise create new UUID
            const mapId = currentMapId || crypto.randomUUID();
            const fixedRoutes = {
                routes: fixedRoutesContext.routes,
                stops: fixedRoutesContext.stops
            };
            // Use PUT /map/{id} for existing zones so Editors on shared maps can save (legacy POST is Owner-only).
            await mapService.saveMapFromLayers(
                effectiveMapName, 
                mapLayers, 
                mapId, 
                global.fleets, 
                thumbnail || undefined,
                fixedRoutes,
                { isEditExistingMap: !!currentMapId }
            );
            
            // If this was a new map, track its ID for future saves
            if (!currentMapId) {
                setCurrentMapId(mapId);
            }
            
            const duration = Date.now() - startTime;
            const messageText = isUpdate
                ? t('layers.save.successUpdate', { name: effectiveMapName })
                : t('layers.save.successNew', { name: effectiveMapName });
            setSaveMessage({type: 'success', text: messageText});
            const savedSignature = buildMapStateSignature({
                mapName: effectiveMapName,
                mapLayers,
                fleets: global.fleets,
                fixedRoutes: {
                    routes: fixedRoutesContext.routes,
                    stops: fixedRoutesContext.stops,
                },
            });
            setLastSavedSignature(savedSignature);
            console.log('✅ Map saved successfully');
            
            // Track save success
            emitMapSaveSuccessAnalytics({
                mapId,
                layerCount: mapLayers.length,
                isUpdate,
                durationMs: duration,
            });
            
        } catch (err) {
            console.error('Error saving map:', err);
            const errorMessage = err instanceof Error ? err.message : t('layers.save.errorGeneric');

            // If user cannot edit the currently loaded shared map, save a new copy instead.
            if (currentMapId && /owner\s+or\s+editor/i.test(errorMessage)) {
                try {
                    const newMapId = crypto.randomUUID();
                    const fixedRoutes = {
                        routes: fixedRoutesContext.routes,
                        stops: fixedRoutesContext.stops
                    };
                    await mapService.saveMapFromLayers(
                        effectiveMapName,
                        mapLayers,
                        newMapId,
                        global.fleets,
                        undefined,
                        fixedRoutes,
                        { isEditExistingMap: false }
                    );
                    setCurrentMapId(newMapId);
                    const savedSignature = buildMapStateSignature({
                        mapName: effectiveMapName,
                        mapLayers,
                        fleets: global.fleets,
                        fixedRoutes: {
                            routes: fixedRoutesContext.routes,
                            stops: fixedRoutesContext.stops,
                        },
                    });
                    setLastSavedSignature(savedSignature);
                    setSaveMessage({
                        type: 'success',
                        text: t('layers.save.successCopy'),
                    });
                    return true;
                } catch (copySaveError) {
                    const copyErrorMessage = copySaveError instanceof Error ? copySaveError.message : errorMessage;
                    setSaveMessage({type: 'error', text: copyErrorMessage});
                    return false;
                }
            }

            setSaveMessage({type: 'error', text: errorMessage});
            
            // Track save failure
            emitMapSaveFailureAnalytics({
                mapId: currentMapId || undefined,
                layerCount: mapLayers.length,
                durationMs: Date.now() - startTime,
                errorCode: err instanceof Error ? err.name : 'unknown_error',
            });
            return false;
        } finally {
            setIsSaving(false);
        }
        return true;
    }, [
        isSaving,
        mapName,
        setMapName,
        currentMapId,
        mapLayers,
        global.fleets,
        fixedRoutesContext.routes,
        fixedRoutesContext.stops,
    ]);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            const isRefreshShortcut = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r');
            if (isRefreshShortcut && hasUnsavedChanges) {
                e.preventDefault();
                setRefreshDraftMapName(mapName.trim());
                setRefreshError(null);
                setRefreshConfirmOpen(true);
                return;
            }

            // Only trigger if no input elements are focused
            if (document.activeElement?.tagName === 'INPUT' || 
                document.activeElement?.tagName === 'TEXTAREA') {
                return;
            }

            // Handle Ctrl+S for save
            if (e.ctrlKey && e.key.toLowerCase() === 's') {
                e.preventDefault();
                handleRealSaveClick();
                return;
            }

            // Layer reordering keyboard shortcuts (Ctrl+Up/Down or Cmd+Up/Down)
            if (global.activeLayerId !== undefined && global.activeLayerId !== null) {
                const activeLayerIndex = mapLayers.findIndex(l => l.layerId === global.activeLayerId);
                
                // Ctrl+Up (or Cmd+Up on Mac) to move active layer up
                if ((e.ctrlKey || e.metaKey) && (e.key === 'ArrowUp' || e.key === 'Up')) {
                    e.preventDefault();
                    if (activeLayerIndex > 0) {
                        reorderLayers(activeLayerIndex, activeLayerIndex - 1);
                    }
                    return;
                }
                
                // Ctrl+Down (or Cmd+Down on Mac) to move active layer down
                if ((e.ctrlKey || e.metaKey) && (e.key === 'ArrowDown' || e.key === 'Down')) {
                    e.preventDefault();
                    if (activeLayerIndex >= 0 && activeLayerIndex < mapLayers.length - 1) {
                        reorderLayers(activeLayerIndex, activeLayerIndex + 1);
                    }
                    return;
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [handleRealSaveClick, global.activeLayerId, mapLayers, reorderLayers, hasUnsavedChanges, mapName]);

    const handleRefreshWithoutSaving = () => {
        bypassBeforeUnloadRef.current = true;
        window.location.reload();
    };

    const handleSearch = useCallback(async () => {
        const query = searchQuery.trim();
        if (!query) {
            setSearchError('Enter a location or point name.');
            return;
        }

        setSearchInProgress(true);
        setSearchError(null);

        try {
            const [bestResult] = await searchAddress(query, { limit: 1, near: mapSettings.mapCentre });
            if (!bestResult) {
                setSearchError(`No results found for "${query}".`);
                return;
            }

            const { lat: latitude, lng: longitude } = bestResult;
            mapSettings.setMapCentre((previous) => ({
                latitude,
                longitude,
                zoom: Math.max(previous.zoom ?? 0, 13),
            }));
        } catch (error) {
            console.warn('Map search failed:', error);
            setSearchError('Location search is unavailable. Try again.');
        } finally {
            setSearchInProgress(false);
        }
    }, [mapSettings, searchQuery]);

    const handleSaveAndRefresh = async () => {
        const trimmedName = refreshDraftMapName.trim();
        if (!trimmedName) {
            setRefreshError(t('layers.refreshDialog.errorEnterName'));
            return;
        }

        setRefreshActionLoading(true);
        setRefreshError(null);
        const didSave = await handleRealSaveClick({ overrideMapName: trimmedName });
        setRefreshActionLoading(false);

        if (!didSave) {
            setRefreshError(t('layers.refreshDialog.errorSaveFailed'));
            return;
        }

        bypassBeforeUnloadRef.current = true;
        window.location.reload();
    };

    if (isCollapsed) {
        return (
            <Box
                sx={{
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            />
        );
    }

    return (
        <>
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                position: 'relative',
                backgroundColor: opsColors.neutral.panel,
                border: 'none',
                borderRadius: 0,
                overflow: 'visible',
                boxShadow: 'none',
            }}
        >
            <Box
                sx={{
                px: 1.75,
                    pt: 2.25,
                    pb: 1.5,
                    borderBottom: `1px solid ${opsColors.neutral.line}`,
                    backgroundColor: opsColors.neutral.panel,
                }}
            >
                <TextField
                    value={searchQuery}
                    onChange={(event) => {
                        setSearchQuery(event.target.value);
                        if (searchError) setSearchError(null);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            void handleSearch();
                        }
                    }}
                    placeholder="Search locations, points..."
                    fullWidth
                    size="small"
                    disabled={searchInProgress}
                    sx={{
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            backgroundColor: opsColors.neutral.panelAlt,
                            '& fieldset': {
                                borderColor: opsColors.neutral.line,
                            },
                            '&:hover fieldset': {
                                borderColor: opsColors.neutral.lineStrong,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: opsColors.brand.primary,
                            },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Icon size={18} name="magnifying-glass" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Search">
                                    <span>
                                        <IconButton
                                            aria-label="Search locations"
                                            onClick={() => void handleSearch()}
                                            disabled={searchInProgress}
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1.5,
                                                color: opsColors.brand.primary,
                                                '&:hover': { backgroundColor: 'rgba(244, 122, 53, 0.08)' },
                                            }}
                                        >
                                            <Icon size={16} name="arrow-right" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                />
                {searchError && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                        {searchError}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <EditableTypography
                            variant="h6"
                            textAlign="left"
                            setValueFunction={editMapTitle}
                            sx={{
                                fontSize: '1.1rem',
                                fontWeight: 700,
                                lineHeight: 1.05,
                                color: 'text.primary',
                            }}
                        >
                            {mapName}
                        </EditableTypography>
                    </Box>
                    {hasUnsavedChanges && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={t('layers.unsavedChanges')}
                            sx={{
                                height: 24,
                                fontWeight: 600,
                                borderColor: opsColors.brand.primary,
                                color: opsColors.brand.primary,
                                backgroundColor: opsColors.neutral.panelAlt,
                            }}
                        />
                    )}
                </Box>

                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25, width: '100%' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, justifyContent: 'flex-start', width: 'fit-content' }}>
                            <Typography variant="body2" sx={{ display: 'block', textAlign: 'left', fontWeight: 600, lineHeight: 1.15, fontSize: '0.9rem', color: 'text.secondary' }}>
                                Layers
                            </Typography>
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 22,
                                    height: 22,
                                    px: 0.55,
                                    borderRadius: '999px',
                                    border: `1px solid ${opsColors.neutral.line}`,
                                    backgroundColor: opsColors.neutral.panelAlt,
                                    color: opsColors.brand.primary,
                                    fontSize: '0.78rem',
                                    lineHeight: 1,
                                    fontWeight: 700,
                                }}
                            >
                                {mapLayers.length}
                            </Box>
                        </Box>
                        <Typography variant="caption" sx={{ ...LAYER_PANEL_CAPTION_SX, mt: 0.25, textAlign: 'left' }}>
                            Drag to reorder
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="Add layer (N)">
                            <IconButton
                                aria-label="Add layer"
                                onClick={() => addLayer()}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1.5,
                                    backgroundColor: opsColors.neutral.panelAlt,
                                    color: opsColors.brand.primary,
                                    '&:hover': { backgroundColor: 'rgba(244, 122, 53, 0.10)' },
                                }}
                            >
                                <Icon size={18} name="circle-plus" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, py: 1 }}>
                <LayersList mapLayers={mapLayers} reorderLayers={reorderLayers} />
            </Box>

            <Box
                sx={{
                    borderTop: `1px solid ${opsColors.neutral.line}`,
                    px: 1.5,
                    py: 1.25,
                }}
            >
                <MapToolBar
                    onImportClick={() => setImportModalOpen(true)}
                    hideAddLayerButton
                />

                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1.25, width: '100%' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%' }}>
                        <FeatureGate featureName="map_save_load">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Tooltip title={currentMapId ? "Update Map (Ctrl+S)" : "Save Map (Ctrl+S)"}>
                                    <span style={{ display: 'block', width: '100%' }}>
                                    <OpsButton
                                        kind="secondary"
                                        size="sm"
                                        icon={isSaving ? <CircularProgress size={14} /> : <Icon d={ICON_FALLBACKS.Save} size={18} />}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => {
                                            void handleRealSaveClick();
                                        }}
                                        disabled={isSaving}
                                    >
                                        {currentMapId ? 'Update' : 'Save'}
                                    </OpsButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </FeatureGate>

                        <FeatureGate featureName="map_save_load">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Tooltip title="Load Maps">
                                    <span style={{ display: 'block', width: '100%' }}>
                                        <OpsButton
                                            kind="secondary"
                                            size="sm"
                                            icon={<Icon size={18} name="folder-open" />}
                                            style={{ width: '100%', justifyContent: 'center' }}
                                            onClick={() => {
                                                logEvent({
                                                    category: 'Map',
                                                    action: 'GalleryOpened',
                                                    label: 'Map Gallery Opened',
                                                });
                                                setIsMapGalleryOpen(true);
                                            }}
                                        >
                                            Load
                                        </OpsButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </FeatureGate>
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        mt: 1.25,
                        pt: 1.25,
                        borderTop: `1px solid ${opsColors.neutral.line}`,
                    }}
                >
                    <FeatureGate featureName="simulations">
                        <OpsButton
                            kind="secondary"
                            size="sm"
                            icon={<Icon size={18} name="rocket" />}
                            style={{
                                width: '100%',
                                justifyContent: 'center',
                            }}
                            onClick={() => openSimulationModal?.()}
                        >
                            Run Simulation
                        </OpsButton>
                    </FeatureGate>
                </Box>

            </Box>
        </Box>
        
        {/* Feedback Snackbar */}
        <Snackbar
            open={!!saveMessage}
            autoHideDuration={4000}
            onClose={() => setSaveMessage(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert 
                onClose={() => setSaveMessage(null)} 
                severity={saveMessage?.type || 'info'}
                sx={{ width: '100%' }}
            >
                {saveMessage?.text}
            </Alert>
        </Snackbar>
        
        {/* Map Gallery Modal */}
        <MapGalleryModal
            open={isMapGalleryOpen}
            onClose={() => setIsMapGalleryOpen(false)}
            hasUnsavedChanges={hasUnsavedChanges}
            currentMapName={mapName}
            onMapNameChange={(value: string) => setMapName(value)}
            onSaveMap={(nameOverride?: string) => handleRealSaveClick({ overrideMapName: nameOverride })}
            onMapLoaded={(mapId: string) => {
                setPendingLoadedMapSignature(currentMapSignature);
                setCurrentMapId(mapId);
            }}
        />
        
        <ImportWizardModal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onImport={(shapes, meta) => {
            handleImport(shapes, meta);
          }}
          existingLayers={mapLayers}
        />

        <Dialog
            open={refreshConfirmOpen}
            onClose={() => {
                if (refreshActionLoading) return;
                setRefreshConfirmOpen(false);
                setRefreshError(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: `linear-gradient(180deg, ${opsColors.neutral.panel} 0%, ${opsColors.neutral.panel} 100%)`,
                    border: `1px solid ${opsColors.neutral.line}`,
                    boxShadow: opsElevation.overlay.shadow,
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <Icon size={20} name="triangle-exclamation" style={{ color: '#ef7335' }} />
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('layers.refreshDialog.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('layers.refreshDialog.subtitle')}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <TextField
                    fullWidth
                    label={t('layers.refreshDialog.mapNameLabel')}
                    value={refreshDraftMapName}
                    onChange={(event) => {
                        setRefreshDraftMapName(event.target.value);
                        if (refreshError) setRefreshError(null);
                    }}
                    onKeyDown={(event) => {
                        event.stopPropagation();
                    }}
                    placeholder={t('layers.refreshDialog.mapNamePlaceholder')}
                    autoFocus
                    margin="dense"
                />
                {refreshError && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                        {refreshError}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={() => {
                        setRefreshConfirmOpen(false);
                        setRefreshError(null);
                    }}
                    disabled={refreshActionLoading}
                >
                    {t('common:cancel')}
                </Button>
                <Button
                    onClick={handleRefreshWithoutSaving}
                    color="inherit"
                    disabled={refreshActionLoading}
                >
                    {t('layers.refreshDialog.refreshWithoutSaving')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSaveAndRefresh}
                    disabled={refreshActionLoading}
                    sx={(theme) => ({
                        backgroundColor: opsColors.brand.primary,
                        '&:hover': { backgroundColor: opsColors.brand.primaryHover },
                    })}
                >
                    {refreshActionLoading ? t('layers.refreshDialog.saving') : t('layers.refreshDialog.saveAndRefresh')}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    )

}

export function emitMapSaveSuccessAnalytics(args: {
    mapId: string;
    layerCount: number;
    isUpdate: boolean;
    durationMs: number;
}) {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.map_save, {
        map_id: args.mapId,
        layer_count: args.layerCount,
        is_update: args.isUpdate,
        duration_ms: args.durationMs,
        timestamp: new Date().toISOString(),
    });
}

export function emitMapSaveFailureAnalytics(args: {
    mapId?: string;
    layerCount: number;
    durationMs: number;
    errorCode: string;
}) {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.map_save_fail, {
        feature_area: 'map_save',
        error_category: 'save_failed',
        error_code: args.errorCode,
        retryable: true,
        map_id: args.mapId,
        layer_count: args.layerCount,
        duration_ms: args.durationMs,
        timestamp: new Date().toISOString(),
    });
}

// Centralized import handler for use in modal and drag-and-drop
export function handleImportedShapes(
    global: any,
    shapes: (Point | Line | Polygon)[],
    layerMeta?: { tripImportSquish?: TripImportSquishMeta }
) {
    if (shapes.length === 0) return;
    global.setLayers((prevLayers: any[]) => {
        const newId = prevLayers.length > 0 ? Math.max(...prevLayers.map(l => l.layerId)) + 1 : 1;
        const normalizedShapes = applyImportedPointLayerColour(shapes, newId);
        const newLayer = {
            ...defaultLayer,
            layerId: newId,
            layerName: `Imported Layer ${newId}`,
            shapes: normalizedShapes,
            ...(layerMeta?.tripImportSquish ? { tripImportSquish: layerMeta.tripImportSquish } : {}),
        };
        return [...prevLayers, newLayer];
    });
    setTimeout(() => {
        const event = new CustomEvent('zoomToLayer', { detail: { shapes } });
        window.dispatchEvent(event);
    }, 0);
    
    // Track layer creation from imported shapes
    const points = shapes.filter(s => s.type === 'point').length;
    const lines = shapes.filter(s => s.type === 'line').length;
    const polygons = shapes.filter(s => s.type === 'polygon').length;
    
    logEvent({
        category: 'Layer Management',
        action: 'LayerCreatedFromImport',
        label: `Imported Layer ${global.mapLayers.length + 1}`,
        layer_id: global.mapLayers.length + 1,
        shapes_imported: shapes.length,
        points_imported: points,
        lines_imported: lines,
        polygons_imported: polygons,
        import_source: 'external_import'
    });
}
